import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import ComposeBox from './ComposeBox';
import PostCard from './PostCard';

export default function Feed({ showToast, onSharePost, currentUser }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedFilter, setFeedFilter] = useState('global'); 

  // Interaction States
    // Post management
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [expandedComments, setExpandedComments] = useState({}); // Tracks which post comments are open
  const [postComments, setPostComments] = useState({}); // Stores fetched comments { postId: [] }
  const [replyTarget, setReplyTarget] = useState({}); // Tracks which comment is being replied to { postId: commentObject }

  // 🆕 BULLETPROOF DEFAULT CHECK
  // Catches 'Disabled', false, 0, or '0' from the database
  const userCommentDefault = !(
    currentUser?.default_comments_state === 'Disabled' || 
    currentUser?.default_comments_state === false || 
    currentUser?.default_comments_state === 0 || 
    currentUser?.default_comments_state === '0'
  );

  // Close dropdown menu when clicking anywhere else on the screen
  useEffect(() => {
    const handleClickOutside = () => setActiveMenuId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchFeed = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/posts?filter=${feedFilter}&limit=20`);
        setPosts(res.data.posts);
      } catch (err) {
        showToast("Could not load feed.");
      } finally {
        setLoading(false);
      }
    };
    fetchFeed();
  }, [feedFilter, showToast]);

  const handlePostSubmit = async (payload) => {
    try {
      const res = await api.post('/posts', {
        body: payload.text,
        audience: payload.audience === 'Everyone' ? 'public' : 'connections',
        image_url: payload.hasImage ? 'https://via.placeholder.com/600x400?text=Sample+Image' : null,
        allow_comments: payload.allowComments
      });
      
      // FIX: Manually inject the known payload state into the returned object 
      // in case the Express route omits the allow_comments column in its response.
      const newPost = {
        ...res.data.post,
        allow_comments: payload.allowComments 
      };

      // Feed.jsx uses setPosts, ProfileView.jsx uses setProfilePosts
      setPosts([newPost, ...posts]);
      showToast('Post published successfully!');
    } catch (err) {
      showToast('Failed to publish post.');
    }
  };

  // --- INTERACTION HANDLERS ---

  const handleVote = async (postId, voteValue) => {
    try {
      // 1. Send the correct numerical payload (1 or -1)
      const response = await api.post(`/posts/${postId}/vote`, { vote_value: voteValue });
      
      // 2. If successful, update the UI with the exact score the database returned
      if (response.data && response.data.status === 'success') {
        const actualDatabaseScore = response.data.new_score;
        
        // Use your specific state setter for this view: setProfilePosts
        setPosts(current => current.map(post => 
          post.id === postId 
            ? { ...post, vote_score: actualDatabaseScore } 
            : post
        ));
      }
    } catch (error) {
      console.error("Vote failed:", error);
      showToast('Failed to register vote'); 
    }
  };

  const toggleComments = async (postId) => {
    const isCurrentlyExpanded = expandedComments[postId];
    
    // Toggle the UI state
    setExpandedComments({ ...expandedComments, [postId]: !isCurrentlyExpanded });

    // If we are opening it and don't have the comments yet, fetch them!
    if (!isCurrentlyExpanded && !postComments[postId]) {
      try {
        const res = await api.get(`/posts/${postId}/comments`);
        setPostComments({ ...postComments, [postId]: res.data.comments });
      } catch (err) {
        showToast("Error loading comments");
      }
    }
  };

  const submitComment = async (postId, text) => {
    if (!text || text.trim() === '') return;
    const targetComment = replyTarget[postId];
  
    try {
      let res;
      if (targetComment) {
        res = await api.post(`/posts/comments/${targetComment.id}/reply`, { body: text });
      } else {
        res = await api.post(`/posts/${postId}/comments`, { body: text });
      }
  
      // FIX: Safely fallback to reply or raw data
      const newComment = res.data?.comment || res.data?.reply || res.data;
      const currentComments = postComments[postId] || [];
      
      setPostComments({ ...postComments, [postId]: [newComment, ...currentComments] });
      setReplyTarget({ ...replyTarget, [postId]: null });
      setPosts(currentPosts => currentPosts.map(p => 
        p.id === postId ? { ...p, comment_count: parseInt(p.comment_count || 0) + 1 } : p
      ));
    } catch (err) {
      showToast("Error posting comment");
    }
  };

  const deletePost = async (postId) => {
    try {
      // Assuming you have a DELETE route in postRoutes.js
      await api.delete(`/posts/${postId}`);
      setPosts(posts.filter(p => p.id !== postId));
      showToast("Post deleted");
    } catch (err) {
      showToast("Error deleting post");
    }
  };


  return (
    <div className="feed-container">
      <ComposeBox 
        onPostSubmit={handlePostSubmit} 
        showToast={showToast} 
        defaultComments={userCommentDefault} 
      />

      <div className="feed-grid" style={{ marginTop: '16px' }}>
        
        {/* MAIN COLUMN: FEED POSTS */}
        <div id="feed-posts" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          
          <div className="feed-tabs" style={{ display: 'flex', gap: '16px', marginBottom: '8px', borderBottom: '1px solid var(--border)' }}>
            <button onClick={() => setFeedFilter('global')} style={{ background: 'none', border: 'none', padding: '8px 0', borderBottom: feedFilter === 'global' ? '2px solid var(--purple)' : '2px solid transparent', cursor: 'pointer', fontWeight: feedFilter === 'global' ? 'bold' : 'normal', color: feedFilter === 'global' ? 'var(--text)' : 'var(--text2)' }}>For You</button>
            <button onClick={() => setFeedFilter('following')} style={{ background: 'none', border: 'none', padding: '8px 0', borderBottom: feedFilter === 'following' ? '2px solid var(--purple)' : '2px solid transparent', cursor: 'pointer', fontWeight: feedFilter === 'following' ? 'bold' : 'normal', color: feedFilter === 'following' ? 'var(--text)' : 'var(--text2)' }}>Following</button>
          </div>

          {loading ? (
            <div className="feed-loading" style={{ textAlign: 'center', padding: '20px', color: 'var(--text3)' }}>Loading the creative horizon...</div>
            ) : posts.length === 0 ? (
              <div className="empty-state" style={{ padding: '40px', textAlign: 'center', color: 'var(--text2)' }}>Your feed is quiet.</div>
            ) : (
              posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  currentUser={currentUser}
                  onVote={handleVote}
                  onSharePost={onSharePost}
                  onDeletePost={deletePost}
                  showToast={showToast}
            
                  // Comment handling
                  isCommentsExpanded={!!expandedComments[post.id]}
                  onToggleComments={toggleComments}
                  postComments={postComments[post.id] || []}
                  onAddComment={submitComment}
            
                  // Reply routing
                  replyTarget={replyTarget[post.id]}
                  onSetReplyTarget={(comment) => setReplyTarget({ ...replyTarget, [post.id]: comment })}
                />
              ))
            )}
        </div>

        {/* RIGHT COLUMN: SIDEBAR */}
        <div className="feed-sidebar">
          <div className="suggested-card">
            <div className="sug-title">Suggested for you</div>
            <div className="sug-item">
              <div className="sug-av" style={{ background: 'var(--amber-l)' }}>🎵</div>
              <div><div className="sug-name">Kofi Asante ✦</div><div className="sug-role">Musician · Accra</div></div>
              <button className="sug-follow" onClick={() => showToast('Following Kofi')}>Follow</button>
            </div>
            <div className="sug-item">
              <div className="sug-av" style={{ background: 'var(--coral-l)' }}>🎭</div>
              <div><div className="sug-name">Zara Kimani</div><div className="sug-role">Actress · Nairobi</div></div>
              <button className="sug-follow" onClick={() => showToast('Following Zara')}>Follow</button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}